// Candidate-mask utilities shared by the logical solver and (eventually) the
// hint controller. Same 9-bit mask convention as `UniquenessChecker.ts`:
// bit `d` represents digit `d`, bit 0 is unused.

import {
  BOARD_SIZE,
  BOX_SIZE,
  CELL_COUNT,
  type Grid,
  type NonEmptyDigit,
} from './types.js';

export const ALL_DIGITS_MASK = 0b1111111110;

export function bit(digit: NonEmptyDigit): number {
  return 1 << digit;
}

export function popcount(x: number): number {
  let n = 0;
  let v = x;
  while (v !== 0) {
    v &= v - 1;
    n++;
  }
  return n;
}

/** Lowest set digit in a non-zero candidate mask. */
export function lowestDigit(mask: number): NonEmptyDigit {
  const lowBit = mask & -mask;
  return (31 - Math.clz32(lowBit)) as NonEmptyDigit;
}

export interface CandidateState {
  readonly rows: number[];
  readonly cols: number[];
  readonly boxes: number[];
  /** Per-cell candidate mask. 0 for filled cells. */
  readonly cells: number[];
}

/** Box index (0..8) for a flat cell index. */
export function boxOfIndex(index: number): number {
  const row = Math.floor(index / BOARD_SIZE);
  const col = index % BOARD_SIZE;
  return Math.floor(row / BOX_SIZE) * BOX_SIZE + Math.floor(col / BOX_SIZE);
}

/** Remove `digitBit` from cell `index`. Returns true iff that bit was set. */
export function eliminateCandidate(
  state: CandidateState,
  index: number,
  digitBit: number,
): boolean {
  const prev = state.cells[index] ?? 0;
  if ((prev & digitBit) === 0) return false;
  state.cells[index] = prev & ~digitBit;
  return true;
}

/** Build per-cell candidate masks from a grid. Returns `null` if `grid`
 * contains a row/col/box duplicate (the grid is already inconsistent). */
export function buildCandidateState(grid: Grid): CandidateState | null {
  const rows = new Array<number>(BOARD_SIZE).fill(0);
  const cols = new Array<number>(BOARD_SIZE).fill(0);
  const boxes = new Array<number>(BOARD_SIZE).fill(0);
  for (let i = 0; i < CELL_COUNT; i++) {
    const d = grid[i] ?? 0;
    if (d === 0) continue;
    const row = Math.floor(i / BOARD_SIZE);
    const col = i % BOARD_SIZE;
    const box = Math.floor(row / BOX_SIZE) * BOX_SIZE + Math.floor(col / BOX_SIZE);
    const b = bit(d as NonEmptyDigit);
    const rm = rows[row] ?? 0;
    const cm = cols[col] ?? 0;
    const bm = boxes[box] ?? 0;
    if ((rm & b) !== 0 || (cm & b) !== 0 || (bm & b) !== 0) return null;
    rows[row] = rm | b;
    cols[col] = cm | b;
    boxes[box] = bm | b;
  }
  const cells = new Array<number>(CELL_COUNT).fill(0);
  for (let i = 0; i < CELL_COUNT; i++) {
    if ((grid[i] ?? 0) !== 0) continue;
    const row = Math.floor(i / BOARD_SIZE);
    const col = i % BOARD_SIZE;
    const box = Math.floor(row / BOX_SIZE) * BOX_SIZE + Math.floor(col / BOX_SIZE);
    const used = (rows[row] ?? 0) | (cols[col] ?? 0) | (boxes[box] ?? 0);
    cells[i] = ALL_DIGITS_MASK & ~used;
  }
  return { rows, cols, boxes, cells };
}

export type UnitKind = 'row' | 'col' | 'box';

export interface Unit {
  readonly kind: UnitKind;
  readonly index: number;
}

/** Cells belonging to `unit`, in canonical order (row: left-to-right; col:
 * top-to-bottom; box: row-major within the 3×3 block). */
export function cellsOfUnit(unit: Unit): number[] {
  const out: number[] = [];
  if (unit.kind === 'row') {
    const r = unit.index;
    for (let c = 0; c < BOARD_SIZE; c++) out.push(r * BOARD_SIZE + c);
  } else if (unit.kind === 'col') {
    const c = unit.index;
    for (let r = 0; r < BOARD_SIZE; r++) out.push(r * BOARD_SIZE + c);
  } else {
    const br = Math.floor(unit.index / BOX_SIZE) * BOX_SIZE;
    const bc = (unit.index % BOX_SIZE) * BOX_SIZE;
    for (let dr = 0; dr < BOX_SIZE; dr++) {
      for (let dc = 0; dc < BOX_SIZE; dc++) {
        out.push((br + dr) * BOARD_SIZE + (bc + dc));
      }
    }
  }
  return out;
}
